import type { AxiosInstance } from "axios";

import type { PaymentProvider } from "@/types/banks";
import type { Dispute, DisputeReason } from "@/types/dispute";
import type { DispatchStatusEnum } from "@/types/order";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PaymentConfig } from "./verify-payments";

import { usePrivateAxios } from "./use-private-axios";

interface DisputesResponse {
	data: Dispute[];
}
async function fetchDisputes(axios: AxiosInstance) {
	await new Promise(resolve => setTimeout(resolve, 100));
	const { data } = await axios.get<DisputesResponse>("/order/list_desputes-for-orders");
	return data;
}

interface DisputeResponse {
	data: Dispute;
}
export function useOrderDisputes() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["disputes"],
		queryFn: () => fetchDisputes(privateAxios)
	});
}
export function useOrderDispute() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["disputes"],
		queryFn: async () => {
			return await privateAxios.get<DisputeResponse>("/order/list_desputes-for-orders");
		}
	});
}

interface DisputeReasonResponse {
	data: DisputeReason[];
}
export function useOrderDisputeReasons() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["disputes"],
		queryFn: async () => {
			const { data } = await privateAxios.get<DisputeReasonResponse>("/order/reasons");
			return data.data;
		}
	});
}

export function useCreateDispute() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["new-dispute"],
		mutationFn: async (data: {
			orderId: string;
			questionId: string;
			otherReason?: string;
			additionalReason?: string;
		}) => {
			const response = await privateAxios.post("/order/dispute", data);
			return response.data;
		},
		onSettled() {
			queryClient.invalidateQueries({ queryKey: "disputes" });
		}

	});
}

export function useAppealDispute() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationFn: async (data: { disputeId: string; appeal: string }) => {
			const response = await privateAxios.put("/order/dispute/appeal-dispute", data);
			return response.data;
		}
	});
};

interface ReturnOrderVariables {
	amount: number;
	orderId: string;
	paymentOption: PaymentProvider;
	newRef: boolean;
}

interface ReturnOrderResponse {
	data: {
		returnOrder: {
			orderAmount: number;
			estimatedCourierFee: number;
			courierFeeDifference: number;
			actualCourierFee: number;
			transactionFee: number;
			contractId: string;
			customerId: string;
			paymentOption: PaymentProvider;
			status: "PENDING";
			paymentRef: string;
			deletedAt: string | null;
			courierFeePaymentDate: string | null;
			courierFeeCollectedBy: string | null;
			courierId: string | null;
			trackingNumber: string | null;
			courierPaymentBalance: string | null;
			createdAt: string;
			updatedAt: string;
			id: string;
			courierFeePaymentStatus: "PENDING" | "PAID" | "FAILED";
			dispatchStatus: DispatchStatusEnum;
		};
		paymentConfig: PaymentConfig;
		totalOrderAmount: number;
		courierFee: number;
		transactionFee: number;
		totalContractValue: number;
	};
};

// const a = {
// 	returnOrder: {
// 		orderAmount: "1000.00",
// 		estimatedCourierFee: "0.00",
// 		courierFeeDifference: "0.00",
// 		actualCourierFee: "0.00",
// 		transactionFee: "60.00",
// 		contractId: "67c0aaf5-feab-4cf4-9227-4144b2313ebc",
// 		customerId: "0fb9c791-571c-4761-8d2b-5f40746f2cbc",
// 		paymentOption: "paystack",
// 		status: "PENDING",
// 		paymentRef: "INT-20250418135954533-NAXH_RTN_ORDER_1744989049391",
// 		deletedAt: null,
// 		courierFeePaymentDate: null,
// 		courierFeeCollectedBy: null,
// 		courierId: null,
// 		trackingNumber: null,
// 		courierPaymentBalance: null,
// 		createdAt: "2025-04-18T15:10:49.395Z",
// 		updatedAt: "2025-04-18T15:10:49.395Z",
// 		id: "6fcc0316-a91c-42d9-a92b-71906d03fe45",
// 		courierFeePaymentStatus: "PENDING",
// 		dispatchStatus: "Pending"
// 	},
// 	paymentConfig: {
// 		publicKey: "pk_test_91da9e655e65545b81b3ecd2b51acf99e45d220e"
// 	},
// 	totalOrderAmount: "1000.00",
// 	courierFee: 0,
// 	transactionFee: "60.00",
// 	totalContractValue: "1000.00"
// };
type OnSuccessCallback = (data: ReturnOrderResponse, variables: ReturnOrderVariables) => void;
export function useInitReturnPayment(OnSuccessCallback: OnSuccessCallback) {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationFn: async (_data: ReturnOrderVariables) => {
			const { amount, ...data } = _data;
			const response = await privateAxios.post<ReturnOrderResponse>("/order/initiate-return-order", data);
			return response.data;
		},
		onSuccess(data, variables) {
			if (OnSuccessCallback)
				OnSuccessCallback(data, variables);
		}
	});
}
